import {
  DateOrNullOrUndefined,
  NumberOrNullOrUndefined,
  StringOrNullOrUndefined,
} from 'src/app/shared/utils';
import { NoteBcbaV2 } from './note-bcba.v2.model';
import { NoteCombinedV2 } from './note-combined.v2.model';
import { ProviderV2 } from './provider.v2.model';
import { PatientV2 } from './patient.v2.model';
import { PlanFirstTrialStep } from './plan-first-trial-step.v2.model';

export class NoteRbtV2 {
  type!: 'rbt';
  id!: number;
  bip_id!: number;
  patient_id!: number;
  client_id!: number;
  patient_name?: string;
  patient_code!: string;
  patient_identifier!: string;
  insurance_identifier!: string;
  doctor_id!: number;

  provider_id!: number;
  provider_name_g!: number;
  provider_credential!: string;
  provider_signature!: string;
  provider_name!: number;
  supervisor_signature!: string;
  supervisor_name!: number;
  supervisor_id!: number;

  session_date!: string | Date;
  participants!: string;
  time_in!: string;
  time_out!: string;
  time_in2!: string;
  time_out2!: string;
  session_length_morning_total!: number;
  session_length_afternon_total!: number;
  session_length_total?: number;
  total_hours!: number;
  total_minutes!: number;
  total_units!: number;
  unit_price!: number | null;
  total_charges!: number | null;
  charges_calculated_at!: string | Date | null;
  charges_locked!: boolean;

  environmental_changes!: string;
  maladaptives!: Maladaptives;
  replacements!: Replacements;
  interventions!: Interventions;
  summary_note!: string;
  meet_with_client_at!: string;
  client_appeared!: string;
  as_evidenced_by!: string;
  rbt_modeled_and_demonstrated_to_caregiver!: string;
  client_response_to_treatment_this_session!: string;
  progress_noted_this_session_compared_to_previous_session!: string;
  next_session_is_scheduled_for!: string | Date;

  billed_blocked!: boolean;
  billed!: boolean;
  paid!: boolean;
  status!: 'pending' | 'ok' | 'no';
  cpt_code!: string;
  location_id!: number;
  md!: string;
  md2!: string;
  md3!: string;
  modifiers!: string;
  valid_modifiers?: { modifier: string; unit_price: number }[];
  created_at!: Date;
  updated_at!: Date;
  deleted_at!: Date;
  pos!: string;
  pa_service_id!: number;
  //
  insurance_id!: number;

  provider?: ProviderV2;
  patient?: PatientV2;
  bacb_credential_number?: string;

  constructor(data: Partial<NoteRbtV2> = {}) {
    const result = {
      ...data,
      type: 'rbt' as const,
      id: NumberOrNullOrUndefined(data.id) ?? 0,
      doctor_id: NumberOrNullOrUndefined(data.doctor_id) ?? 0,
      patient_id: NumberOrNullOrUndefined(data.patient_id) ?? 0,
      patient_name: StringOrNullOrUndefined(data.patient_name),
      insurance_identifier: StringOrNullOrUndefined(data.insurance_identifier) ?? '',
      client_id:
        NumberOrNullOrUndefined(data.client_id) ??
        NumberOrNullOrUndefined(data.patient_id) ?? 0,
      patient_code: data.patient_code
        ? StringOrNullOrUndefined(data.patient_code) ?? ''
        : StringOrNullOrUndefined(data.patient_id) ?? '',
      patient_identifier: data.patient_identifier
        ? StringOrNullOrUndefined(data.patient_identifier) ?? ''
        : StringOrNullOrUndefined(data.patient_id) ?? '',
      bip_id: NumberOrNullOrUndefined(data.bip_id) ?? 0,
      provider_name_g: NumberOrNullOrUndefined(data.provider_name_g) ?? 0,
      provider_id: NumberOrNullOrUndefined(data.provider_id) ?? 0,
      provider_credential:
        data.provider_credential && data.provider_credential !== 'null'
          ? StringOrNullOrUndefined(data.provider_credential) ?? ''
          : '',
      pos: StringOrNullOrUndefined(data.pos) ?? '',
      pa_service_id: NumberOrNullOrUndefined(data.pa_service_id) ?? 0,

      session_date: StringOrNullOrUndefined(data.session_date) ?? '',
      participants: StringOrNullOrUndefined(data.participants) ?? '',
      time_in: StringOrNullOrUndefined(data.time_in) ?? '',
      time_out: StringOrNullOrUndefined(data.time_out) ?? '',
      time_in2: StringOrNullOrUndefined(data.time_in2) ?? '',
      time_out2: StringOrNullOrUndefined(data.time_out2) ?? '',
      session_length_morning_total: NoteRbtV2.calculateSessionLength(data),
      session_length_afternon_total: NoteRbtV2.calculateSessionLength2(data),
      total_hours: NoteRbtV2.calculateTotalHours(data),
      total_minutes: NoteRbtV2.calculateTotalMinutes(data),
      total_units: NoteRbtV2.calculateTotalUnits(data),
      unit_price: NumberOrNullOrUndefined(data.unit_price),
      total_charges: NumberOrNullOrUndefined(data.total_charges),
      charges_calculated_at: DateOrNullOrUndefined(data.charges_calculated_at),
      charges_locked: Boolean(data.charges_locked),
      environmental_changes: StringOrNullOrUndefined(data.environmental_changes) ?? '',
      maladaptives: (Array.isArray(data.maladaptives) ? data.maladaptives : []) as Maladaptives,
      replacements: (Array.isArray(data.replacements) ? data.replacements : []) as Replacements,
      interventions: (Array.isArray(data.interventions) ? data.interventions : []) as Interventions,
      summary_note: StringOrNullOrUndefined(data.summary_note) ?? '',
      meet_with_client_at: StringOrNullOrUndefined(data.meet_with_client_at) ?? '',
      client_appeared: StringOrNullOrUndefined(data.client_appeared) ?? '',
      as_evidenced_by: StringOrNullOrUndefined(data.as_evidenced_by) ?? '',
      rbt_modeled_and_demonstrated_to_caregiver: StringOrNullOrUndefined(
        data.rbt_modeled_and_demonstrated_to_caregiver
      ) ?? '',
      client_response_to_treatment_this_session: StringOrNullOrUndefined(
        data.client_response_to_treatment_this_session
      ) ?? '',
      progress_noted_this_session_compared_to_previous_session:
        StringOrNullOrUndefined(
          data.progress_noted_this_session_compared_to_previous_session
        ) ?? '',
      next_session_is_scheduled_for: StringOrNullOrUndefined(
        data.next_session_is_scheduled_for
      ) ?? '',
      provider_signature: StringOrNullOrUndefined(data.provider_signature) ?? '',
      provider_name: NumberOrNullOrUndefined(data.provider_name) ?? 0,
      supervisor_signature: StringOrNullOrUndefined(data.supervisor_signature) ?? '',
      supervisor_name: NumberOrNullOrUndefined(data.supervisor_name) ?? 0,
      supervisor_id: NumberOrNullOrUndefined(data.supervisor_id) ?? 0,
      billed_blocked: Boolean(data.billed_blocked),
      billed: Boolean(data.billed),
      paid: Boolean(data.paid),
      status: (StringOrNullOrUndefined(data.status) as 'pending') ?? 'pending',
      cpt_code: StringOrNullOrUndefined(data.cpt_code) ?? '',
      location_id: NumberOrNullOrUndefined(data.location_id) ?? 0,
      md: StringOrNullOrUndefined(data.md) ?? '',
      md2: StringOrNullOrUndefined(data.md2) ?? '',
      md3: StringOrNullOrUndefined(data.md3) ?? '',
      modifiers: StringOrNullOrUndefined(data.modifiers) ?? '',
      valid_modifiers: (data.valid_modifiers as { modifier: string; unit_price: number }[]) ?? undefined,

      created_at: DateOrNullOrUndefined(data.created_at) ?? new Date(),
      updated_at: DateOrNullOrUndefined(data.updated_at) ?? new Date(),
      deleted_at: DateOrNullOrUndefined(data.deleted_at) ?? new Date(),
      //
      insurance_id: NumberOrNullOrUndefined(data.insurance_id) ?? 0,
      bacb_credential_number: StringOrNullOrUndefined(
        data.bacb_credential_number ?? '',
      ),
    };
    // Post Work
    result.session_length_morning_total =
      NoteRbtV2.calculateSessionLength(data);
    result.session_length_afternon_total =
      NoteRbtV2.calculateSessionLength2(data);
    result.session_length_total =
      result.session_length_morning_total +
      result.session_length_afternon_total;

    result.total_hours = NoteRbtV2.calculateTotalHours(data);
    result.total_minutes = NoteRbtV2.calculateTotalMinutes(data);
    result.total_units = NoteRbtV2.calculateTotalUnits(data);
    Object.assign(this, result);
  }

  static calculateSessionLength(data: Partial<NoteRbtV2>): number {
    if (data.session_length_morning_total)
      return NumberOrNullOrUndefined(data.session_length_morning_total) ?? 0;
    return 0;
  }
  static calculateSessionLength2(data: Partial<NoteRbtV2>): number {
    if (data.session_length_afternon_total)
      return NumberOrNullOrUndefined(data.session_length_afternon_total) ?? 0;
    return 0;
  }
  static calculateTotalHours(data: Partial<NoteRbtV2>): number {
    if (data.total_hours) return NumberOrNullOrUndefined(data.total_hours) ?? 0;
    return 0;
  }
  static calculateTotalMinutes(data: Partial<NoteRbtV2>): number {
    if (data.total_minutes) return NumberOrNullOrUndefined(data.total_minutes) ?? 0;
    return 0;
  }
  static calculateTotalUnits(data: Partial<NoteRbtV2>): number {
    if (data.total_units) return NumberOrNullOrUndefined(data.total_units) ?? 0;
    return 0;
  }
}

export type Maladaptives = {
  id: number | null;
  name: string;
  ocurrences: number;
  baseline?: number;
  baseline_date?: Date;
}[];

export type Replacements = {
  id: number | null;
  name: string;
  total_trials?: number;
  total_steps?: number;
  correct_responses?: number;
  is_required?: boolean;
  first_trial?: boolean;
  first_trial_steps?: PlanFirstTrialStep[];
}[];

export type Interventions = string[];

export function isNoteRbtV2(data: NoteRbtV2 | NoteBcbaV2 | NoteCombinedV2): data is NoteRbtV2 {
  return data?.type === 'rbt';
}
